import React from 'react';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as MessageIcon } from 'assets/icons/chat.svg';
import CloseIcon from '@material-ui/icons/Close';
import Room from './components/Room';
import { useQuery, useSubscription } from 'react-apollo';
import Spin from 'components/common/Spin';
import { GET_ROOMS_BY_STUDENT, LISTEN_USER_UNREAD_COUNT } from 'graphql/chat';
import { getInitials } from 'utils';
import stringToColor from 'string-to-color';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 600,
    width: '100%',
    overflow: 'hidden',
    // [theme.breakpoints.down('sm')]: {
    //   width
    // }
  },
  header: {
    padding: '0 24px',
  },
  heading: {
    fontSize: 24,
  },
  tabContent: {
    flex: 1,
  },
  badge: {
    fontSize: 12,
    minWidth: 16,
    height: 16,
  },
  msgIcon: {
    '& path': {
      opacity: 0.5,
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      {...other}
    >
      {children}
    </div>
  );
}

const ChatMessages = ({ show, toggle }) => {
  const classes = useStyles();
  const [tab, setTab] = React.useState(0);
  const user = useSelector((s) => s.user.user);
  const profile = useSelector((s) => s.student.profile);
  const { data, loading } = useQuery(GET_ROOMS_BY_STUDENT, {
    variables: { userId: user.id },
  });
  const { data: unread } = useSubscription(LISTEN_USER_UNREAD_COUNT, {
    variables: { userId: user.id },
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const totalCount = unread?.v_chat_unread_counts.reduce((memo, r) => {
    return memo + r.count;
  }, 0);

  const coachRoom = data?.rooms.find((r) => r.room.room_name === 'coaches');
  const adminRoom = data?.rooms.find((r) => r.room.room_name === 'admin');
  const groupRoom = data?.rooms.find((r) => r.room.room_name === 'groups');

  return (
    <>
      <IconButton onClick={toggle}>
        <Badge
          badgeContent={totalCount || 0}
          color="secondary"
          classes={{
            badge: classes.badge,
          }}
        >
          <MessageIcon className={classes.msgIcon} />
        </Badge>
      </IconButton>
      <Drawer
        keepMounted
        classes={{
          paper: classes.root,
        }}
        anchor="right"
        open={show}
        onClose={toggle}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          paddingLeft={2}
          paddingTop={1}
          marginBottom={{xs:0, md: 3}}
        >
          <Typography variant="h6" className={classes.heading}>
            Chat
          </Typography>
          <IconButton onClick={toggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column">
          <Box boxShadow="0px 8px 8px rgba(98, 108, 130, 0.2)" zIndex={1}>
            <Box className={classes.header}>
              <Tabs
                value={tab}
                indicatorColor="primary"
                textColor="primary"
                variant="standard"
                onChange={handleTabChange}
              >
                {(profile.product_type !== 'solo' && profile.product_type !== 'cohort') && (
                  <Tab
                    label={
                      profile.lc_id && profile.hc_id ? (
                        <Box display="flex" alignItems="center">
                          <AvatarGroup
                            spacing="small"
                            style={{ marginLeft: 8 }}
                          >
                            <Avatar src={profile.lc_user.user_pic}>
                              {getInitials(profile.lc_user.user_first_name)}
                            </Avatar>
                            <Avatar src={profile.hc_user.user_pic}>
                              {getInitials(profile.hc_user.user_first_name)}
                            </Avatar>
                          </AvatarGroup>
                          <Typography
                            color="inherit"
                            variant="subtitle2"
                            style={{ marginLeft: 8 }}
                          >
                            {profile.lc_user.user_first_name} &{' '}
                            {profile.hc_user.user_first_name}
                          </Typography>
                        </Box>
                      ) : (
                        'Coaches'
                      )
                    }
                  />
                )}
                <Tab
                  label={
                    profile.admin ? (
                      <Box display="flex" alignItems="center">
                        <Avatar src={profile.admin.user_pic}>
                          {getInitials(
                            profile.admin.user_first_name,
                            profile.admin.user_last_name,
                          )}
                        </Avatar>
                        <Typography
                          color="inherit"
                          variant="subtitle2"
                          style={{ marginLeft: 8 }}
                        >
                          {profile.admin.user_first_name}
                        </Typography>
                      </Box>
                    ) : (
                      'Admin'
                    )
                  }
                />
                {profile.group && profile.group?.group_id && (
                  <Tab
                    label={
                      <Box display="flex" alignItems="center">
                        <Avatar
                          style={{
                            backgroundColor: stringToColor(
                              profile.group.group.name,
                            ),
                          }}
                        >
                          {getInitials(profile.group.group.name)}
                        </Avatar>
                        <Typography
                          color="inherit"
                          variant="subtitle2"
                          style={{ marginLeft: 8 }}
                        >
                          {profile.group.group.name}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              </Tabs>
            </Box>
          </Box>
          {loading ? (
            <Spin spinning linear />
          ) : (
            <>
              {[coachRoom, adminRoom, groupRoom]
                .filter((i) => i)
                .filter((i) =>
                  profile.product_type === 'solo' || profile.product_type === 'cohort'
                    ? i.room.room_name !== 'coaches'
                    : i,
                )
                .map((i, idx) => (
                  <TabPanel
                    className={classes.tabContent}
                    value={tab}
                    index={idx}
                    key={i.id}
                  >
                    <Room
                      user={user}
                      profile={profile}
                      tab={idx}
                      room={i.room}
                      isFocused={idx === tab && show}
                    />
                  </TabPanel>
                ))}
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default ChatMessages;
